import React, { useRef, useEffect } from 'react';
import { Parallax } from 'react-scroll-parallax';
import { motion, useScroll, useTransform } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useShouldReduceAnimations } from '../hooks/useDeviceDetection';

// Register GSAP ScrollTrigger
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

type AnimatedBackgroundProps = {
  variant?: 'hero' | 'section' | 'gradient';
  children?: React.ReactNode;
  className?: string;
};

/**
 * Enhanced animated background component using multiple animation libraries
 * Combines Framer Motion, GSAP ScrollTrigger, and React Scroll Parallax
 * Optimized for mobile performance
 */
export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({ 
  variant = 'hero', 
  children,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const shouldReduceAnimations = useShouldReduceAnimations();
  
  // Parallax transforms - disabled on mobile for performance
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]);

  useEffect(() => {
    // Skip heavy animations on mobile
    if (!containerRef.current || shouldReduceAnimations) return;

    // GSAP ScrollTrigger animations for floating elements
    const particles = containerRef.current.querySelectorAll('.particle');
    
    particles.forEach((particle, index) => {
      gsap.to(particle, {
        y: 'random(-100, 100)',
        x: 'random(-100, 100)',
        rotation: 'random(-180, 180)',
        duration: 'random(3, 8)',
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: index * 0.2,
      });
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [shouldReduceAnimations]);

  const renderParticles = () => {
    // Reduce particle count on mobile for better performance
    const particleCount = shouldReduceAnimations 
      ? 0 // No particles on mobile
      : variant === 'hero' ? 20 : 10;
    
    return Array.from({ length: particleCount }).map((_, i) => (
      <motion.div
        key={i}
        className="particle absolute rounded-full bg-white/20 blur-sm"
        style={{
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: [0.2, 0.6, 0.2],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: Math.random() * 3 + 2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: Math.random() * 2,
        }}
      />
    ));
  };

  if (variant === 'hero') {
    return (
      <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
        {/* Animated gradient background - simplified on mobile */}
        <motion.div
          className="absolute inset-0"
          style={{
            opacity: shouldReduceAnimations ? 1 : opacity,
            background: 'linear-gradient(180deg, #000000 0%, #0F2A7D 50%, #12182E 100%)'
          }}
        />
        
        {/* Parallax layers - disabled on mobile */}
        {!shouldReduceAnimations && (
          <>
            {/* Parallax layer 1 - Slow moving */}
            <Parallax speed={-20} className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-indigo-600/10" />
            </Parallax>

            {/* Parallax layer 2 - Medium speed */}
            <Parallax speed={-10} className="absolute inset-0">
              <div className="absolute inset-0">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
              </div>
            </Parallax>

            {/* Floating particles */}
            <div className="absolute inset-0 pointer-events-none">
              {renderParticles()}
            </div>

            {/* Grid overlay with parallax */}
            <Parallax speed={-5} className="absolute inset-0 opacity-10">
              <div 
                className="absolute inset-0" 
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
                  `,
                  backgroundSize: '50px 50px'
                }}
              />
            </Parallax>
          </>
        )}

        {/* Simplified static background for mobile */}
        {shouldReduceAnimations && (
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          </div>
        )}

        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </div>
    );
  }

  if (variant === 'section') {
    return (
      <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
        {/* Parallax disabled on mobile for better performance */}
        {shouldReduceAnimations ? (
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-400/20 rounded-full blur-2xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-400/20 rounded-full blur-2xl" />
            </div>
          </div>
        ) : (
          <Parallax speed={-8} className="absolute inset-0 opacity-30">
            <div className="absolute inset-0">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-400/20 rounded-full blur-2xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-400/20 rounded-full blur-2xl" />
            </div>
          </Parallax>
        )}
        
        <div className="relative z-10">
          {children}
        </div>
      </div>
    );
  }

  // Gradient variant
  return (
    <div className={`relative ${className}`}>
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};
